import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`position: fixed; inset: 0; z-index: 1200; background: #10212dc4; display: flex; justify-content: center; align-items: center; padding: 12px;`;
const Dialog = styled.section`background: #fff; color: #172b38; border-radius: 8px; width: min(1340px, 100%); max-height: calc(100dvh - 24px); overflow: auto; padding: 18px; box-sizing: border-box; h2 { margin: 0 0 8px; } p { margin: 8px 0; }`;
const Controls = styled.div`display: flex; gap: 10px; flex-wrap: wrap; margin: 12px 0; align-items: center; button { padding: 9px 14px; border: 1px solid #b5c0c9; background: #fff; color: #17384a; border-radius: 4px; cursor: pointer; } button[data-primary] { background: #196780; border-color: #196780; color: #fff; } button:disabled { opacity: .5; cursor: not-allowed; }`;
const Notice = styled.p`padding: 10px 12px; background: ${({ $error }) => $error ? '#fff0ee' : '#edf5f8'}; border: 1px solid ${({ $error }) => $error ? '#e7b4ad' : '#c9dce4'}; border-radius: 4px; overflow-wrap: anywhere;`;
const Browser = styled.div`position: relative; width: 100%; max-width: 1280px; border: 2px solid #9aaeb9; background: #edf1f4; line-height: 0; touch-action: none; user-select: none; outline-offset: 3px; img { display: block; width: 100%; height: auto; aspect-ratio: 1280/900; pointer-events: none; } &:focus { outline: 3px solid #196780; }`;
const Summary = styled.div`display: flex; gap: 16px; flex-wrap: wrap; font-size: .88rem; margin: 10px 0;`;
const terminal = new Set(['completed', 'cancelled', 'expired', 'failed']);
const browserKeys = new Set(['Backspace', 'Tab', 'Enter', 'Delete', 'Insert', 'Home', 'End',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Shift', 'CapsLock']);
const safeButton = (button) => ({ 0: 'left', 1: 'middle', 2: 'right' }[button] || 'left');
const position = (event) => {
  const picture = event.currentTarget.querySelector('img')?.getBoundingClientRect();
  const box = picture?.width ? picture : event.currentTarget.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(1279, Math.round((event.clientX - box.left) * 1280 / box.width))),
    y: Math.max(0, Math.min(899, Math.round((event.clientY - box.top) * 900 / box.height))),
  };
};

export default function AssistedDanlonSession({ initialJob, onClose, onUpdated, service, flow, Review, introNotice }) {
  const [job, setJob] = useState(initialJob);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [frameError, setFrameError] = useState('');
  const [inputFailed, setInputFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [absenceAcknowledged, setAbsenceAcknowledged] = useState(false);
  const [frameUrl, setFrameUrl] = useState('');
  const live = useRef(true);
  const frame = useRef(null);
  const dialog = useRef(null);
  const priorFocus = useRef(null);
  const queue = useRef(Promise.resolve());
  const queuedInputs = useRef(0);
  const inputBlocked = useRef(false);
  const generation = useRef(0);
  const pointer = useRef(null);
  const lastMove = useRef(0);
  const heldKeys = useRef(new Set());
  const jobRef = useRef(job);
  jobRef.current = job;
  const interactive = Boolean(job.canInteract) && !busy && !inputFailed;
  const finished = terminal.has(job.status) || Boolean(job.closedAt);
  const browserVisible = !finished && Boolean(job.canViewBrowser ?? job.canInteract);

  useEffect(() => {
    live.current = true;
    priorFocus.current = document.activeElement;
    dialog.current?.focus();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      live.current = false; generation.current += 1; inputBlocked.current = true;
      document.body.style.overflow = oldOverflow;
      priorFocus.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (finished) return undefined;
    let stopped = false; let timer; const controller = new AbortController();
    const poll = async () => {
      try {
        const result = await service.get(job.id, controller.signal);
        if (!stopped) { setJob(result.job); setStatusError(''); }
      } catch (failure) {
        if (!stopped) setStatusError(flow.pollError);
      } finally { if (!stopped) timer = setTimeout(poll, 1800); }
    };
    timer = setTimeout(poll, 1200);
    return () => { stopped = true; clearTimeout(timer); controller.abort(); };
  }, [job.id, job.status, finished, service, flow.pollError]);

  useEffect(() => {
    if (!browserVisible) return undefined;
    let stopped = false; let timer; let currentUrl; const controller = new AbortController();
    const refresh = async () => {
      try {
        const blob = await service.frame(job.id, controller.signal);
        if (!stopped) {
          const next = URL.createObjectURL(blob);
          if (currentUrl) URL.revokeObjectURL(currentUrl);
          currentUrl = next; setFrameUrl(next); setFrameError('');
        }
      } catch (failure) {
        if (!stopped) setFrameError('The live browser view is unavailable. Controls are paused until a fresh view arrives.');
      } finally { if (!stopped) timer = setTimeout(refresh, 200); }
    };
    refresh();
    return () => {
      stopped = true; controller.abort(); clearTimeout(timer);
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setFrameUrl('');
    };
  }, [job.id, browserVisible, service]);

  // Input requests must reach Chromium in order (down/move/up, modifiers,
  // paste). Never retry input: a retry can click or submit a form twice.
  const send = useCallback((input) => {
    if (!live.current || inputBlocked.current || !jobRef.current.canInteract) return;
    if (input.kind === 'move' && queuedInputs.current > 2) return;
    if (queuedInputs.current >= 128) {
      inputBlocked.current = true;
      setInputFailed(true);
      setError('The browser connection is too slow for further input. Refresh status and check the current Danløn screen before continuing.');
      return;
    }
    queuedInputs.current += 1;
    const token = generation.current;
    queue.current = queue.current.then(async () => {
      try {
        if (!live.current || inputBlocked.current || token !== generation.current) return;
        await service.input(jobRef.current.id, input);
      }
      catch (failure) {
        inputBlocked.current = true;
        if (live.current) {
          setInputFailed(true);
          setError('Browser input was interrupted. It was not repeated. Refresh status before continuing.');
        }
      }
      finally { queuedInputs.current -= 1; }
    });
  }, [service]);

  const refreshStatus = async () => {
    setBusy(true);
    try {
      const result = await service.get(job.id);
      if (!live.current) return;
      generation.current += 1; inputBlocked.current = false; pointer.current = null;
      setInputFailed(false); setJob(result.job); setError(''); setStatusError('');
    } catch (failure) { if (live.current) setError(failure.message); }
    finally { if (live.current) setBusy(false); }
  };

  const action = async (name) => {
    setBusy(true); setError('');
    try {
      // Wait for the last already-sent input before transitioning the job.
      await queue.current;
      if (name === 'continue' && inputBlocked.current) throw new Error('Refresh status and check the Danløn screen before continuing after interrupted input.');
      generation.current += 1;
      const continuation = job.requiresCreationAbsenceConfirmation
        ? { confirmMissingCreation: absenceAcknowledged } : flow.continuePayload?.(job);
      const result = name === 'continue' && continuation
        ? await service[name](job.id, continuation) : await service[name](job.id);
      if (!live.current) return;
      setAbsenceAcknowledged(false);
      setJob(result.job); onUpdated?.(result.job);
    } catch (failure) { if (live.current) setError(failure.message); }
    finally { if (live.current) setBusy(false); }
  };

  const closeSession = async () => {
    setBusy(true); setError('');
    try {
      await service.closeSession(job.browserSessionId);
      if (live.current) setJob(current => ({ ...current, sessionReusable: false }));
    } catch (failure) { if (live.current) setError(failure.message); }
    finally { if (live.current) setBusy(false); }
  };

  const enabled = interactive && frameUrl && !frameError;
  const browserKey = (event, up) => {
    if (!enabled) return;
    if (event.key === 'Escape') { event.preventDefault(); event.currentTarget.blur(); dialog.current?.focus(); return; }
    if (event.key === 'Process' || event.key === 'Dead' || event.isComposing) return;
    // Keep host clipboard modifiers local. Forwarding Control/Meta would be
    // rejected by the worker and prevent the subsequent native paste event.
    if (['Control', 'Meta', 'Alt', 'AltGraph'].includes(event.key)) return;
    // Paste is forwarded once by onPaste, never also as a remote Ctrl+V.
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') return;
    event.preventDefault(); event.stopPropagation();
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (Array.from(event.key).length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (!up) send({ kind: 'text', text: event.key });
    } else if (browserKeys.has(event.key)) {
      if (up) heldKeys.current.delete(event.key); else heldKeys.current.add(event.key);
      send({ kind: up ? 'keyUp' : 'keyDown', key: event.key });
    }
  };

  return <Overlay>
    <Dialog ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={flow.titleId} onKeyDown={(event) => {
      if (event.key !== 'Tab' || event.target === frame.current) return;
      const controls = [...dialog.current.querySelectorAll('summary, button:not(:disabled), input:not(:disabled), [tabindex="0"]')];
      if (!controls.length) return;
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && (event.target === first || event.target === dialog.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
    }}>
      <h2 id={flow.titleId}>{flow.title}</h2>
      <p>Log in yourself if prompted and complete any CAPTCHA in the private browser below. Your Danløn password is not saved in Tempus settings. The private browser stays open between tasks until you close it. Danløn may require you to log in again if its own login expires.</p>
      <p>{flow.purpose}</p>
      {introNotice}
      <Summary><span>Payroll: {job.year}-{String(job.month).padStart(2, '0')}</span><span>{flow.employeeSummary(job)}</span><span>Job: {job.id}</span></Summary>
      <Notice role="status"><strong>{flow.labels[job.status] || 'Checking workflow status'}</strong>{job.message && <><br />{job.message}</>}</Notice>
      {error && <Notice $error role="alert">{error}</Notice>}
      {statusError && <Notice $error role="alert">{statusError}</Notice>}
      {job.status === 'completed' && <Notice>{flow.completedNotice}</Notice>}
      {finished && job.sessionReusable && <p>The Danløn login remains available. Close this window to use it for another Danløn task.</p>}
      {job.blockedCode && <p>Reference: <code>{job.blockedCode}</code></p>}
      {Review && <Review job={job} />}
      {job.requiresCreationAbsenceConfirmation && <label><input type="checkbox" checked={absenceAcknowledged} onChange={event => setAbsenceAcknowledged(event.target.checked)} /> I checked in Danløn that the proposed employee number was not created or archived. Allow a new reviewed creation attempt.</label>}
      <Controls>
        {!finished && job.canContinue && <button data-primary disabled={busy || inputFailed || (job.requiresCreationAbsenceConfirmation && !absenceAcknowledged)} onClick={() => action('continue')}>{job.continueLabel || flow.continueLabel(job)}</button>}
        <button disabled={busy} onClick={refreshStatus}>Refresh status</button>
        {!finished && <button disabled={busy} onClick={() => action('cancel')}>Cancel session</button>}
        {finished && job.sessionReusable && job.browserSessionId && <button disabled={busy} onClick={closeSession}>Close Danløn session</button>}
        <button disabled={busy} onClick={async () => { setBusy(true); await queue.current; if (live.current) onClose(job); }}>{finished ? 'Close' : 'Hide progress'}</button>
      </Controls>
      {!finished && <p style={{ fontSize: '.85rem' }}>{flow.resumeNotice}</p>}
      {browserVisible && <>
        <p id="danlon-browser-help" style={{ fontSize: '.85rem' }}>{job.canInteract
          ? 'Click inside to control Danløn. Type or paste normally. For dropdowns, click the field, use the arrow keys, then press Enter. Tab moves between Danløn fields; Escape returns to Tempus controls. Complete CAPTCHA yourself. Never share this screen while entering credentials.'
          : 'Danløn is being checked or updated. Browser input is paused while this step runs.'}</p>
        {frameError && <Notice $error role="alert">{frameError}</Notice>}
        <Browser ref={frame} data-mirror-private="true" tabIndex={0} role="application" aria-label="Private Danløn browser" aria-describedby="danlon-browser-help" aria-disabled={!enabled}
          onBlur={() => { heldKeys.current.forEach((key) => send({ kind: 'keyUp', key })); heldKeys.current.clear(); }}
          onContextMenu={(event) => event.preventDefault()}
          onKeyDown={(event) => browserKey(event, false)} onKeyUp={(event) => browserKey(event, true)}
          onPaste={(event) => { if (!enabled) return; event.preventDefault(); const text = event.clipboardData.getData('text'); if (text) send({ kind: 'text', text }); }}
          onPointerDown={(event) => {
            if (!enabled) return; event.preventDefault(); event.currentTarget.focus();
            event.currentTarget.setPointerCapture?.(event.pointerId); pointer.current = { id: event.pointerId, button: safeButton(event.button) };
            send({ kind: 'down', ...position(event), button: safeButton(event.button) });
          }}
          onPointerMove={(event) => { if (!enabled || Date.now() - lastMove.current < 80) return; lastMove.current = Date.now(); send({ kind: 'move', ...position(event) }); }}
          onPointerUp={(event) => { if (!enabled || !pointer.current) return; send({ kind: 'up', ...position(event), button: pointer.current.button }); pointer.current = null; event.currentTarget.releasePointerCapture?.(event.pointerId); }}
          onPointerCancel={(event) => { if (pointer.current) send({ kind: 'up', ...position(event), button: pointer.current.button }); pointer.current = null; }}
          onWheel={(event) => { if (!enabled) return; send({ kind: 'wheel', ...position(event), deltaX: Math.max(-2000, Math.min(2000, event.deltaX)), deltaY: Math.max(-2000, Math.min(2000, event.deltaY)) }); }}>
          {frameUrl ? <img src={frameUrl} alt={flow.frameAlt} draggable={false} /> : <p style={{ lineHeight: 1.5, padding: 30 }}>Loading private browser…</p>}
        </Browser>
      </>}
      {(job.sessionExpiresAt || job.expiresAt) && (!finished || job.sessionReusable) && <p style={{ fontSize: '.8rem' }}>Session expires: {new Date(job.sessionExpiresAt || job.expiresAt).toLocaleString()}</p>}
    </Dialog>
  </Overlay>;
}
